import SaveBar from '@/components/forms/SaveBar'
import ImageTileControls from '@/components/media/ImageTileControls'
import LazyImageEditorDialog from '@/components/media/LazyImageEditorDialog'
import { MediaPlaceholder } from '@/components/patterns/media-placeholder'
import { StockImageLicensingNotice } from '@/components/stock-images/StockImageLicensingNotice'
import { StockTagManager } from '@/components/stock-images/StockTagManager'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { FileDropzone } from '@/components/ui/file-dropzone'
import { Typography } from '@/components/ui/typography'
import { usePathHelper } from '@/hooks/usePathHelper'
import { useIsReadOnly } from '@/lib/casl'
import { MediaUploadUsage } from '@/lib/enums/MediaUploadUsage'
import { useAppForm } from '@/lib/form'
import { canEditMedia } from '@/lib/media-editing'
import { getMediaUploadProfile } from '@/lib/media-upload-profiles'
import {
  useCreateStockImageMutation,
  useStockImageTags,
  useUpdateStockImageMutation,
} from '@/lib/services/stock-images/stock-images.query'
import {
  stockImageCreateSchema,
  stockImageSchema,
} from '@/lib/services/stock-images/stock-images.schema'
import { getErrorMessage } from '@/lib/utils'
import {
  flattenMerchandisingTagGroups,
  splitMerchandisingTagIds,
} from '@/lib/services/merchandising-tags/merchandising-tags.utils'
import { useStore } from '@tanstack/react-form'
import { useNavigate } from '@tanstack/react-router'
import { ImageIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

const stockCategoryTagType = 'stock_category'
const occasionTagType = 'occasion'
const seasonTagType = 'season'
const bannerTagKey = 'stock_usage_banner'

function getAssetMedia(asset) {
  return asset?.latest_media || asset?.original_media || {}
}

function selectedTagIdsForStockImage(stockImage) {
  return (stockImage?.tags ?? [])
    .filter((tag) => tag.tag_type === stockCategoryTagType && tag.is_active === true)
    .map((tag) => Number(tag.id))
    .filter((id) => id > 0)
}

function selectedOccasionIdsForStockImage(stockImage) {
  return (stockImage?.tags ?? [])
    .filter((tag) => tag.tag_type === occasionTagType && tag.is_active === true)
    .map((tag) => Number(tag.id))
    .filter((id) => id > 0)
}

function selectedSeasonIdsForStockImage(stockImage) {
  return (stockImage?.tags ?? [])
    .filter((tag) => tag.tag_type === seasonTagType && tag.is_active === true)
    .map((tag) => Number(tag.id))
    .filter((id) => id > 0)
}

function formValuesForStockImage(stockImage) {
  return {
    file: null,
    title: stockImage?.title ?? '',
    description: stockImage?.description ?? '',
    is_published: stockImage?.is_published ?? true,
    tag_ids: selectedTagIdsForStockImage(stockImage),
    occasion_ids: selectedOccasionIdsForStockImage(stockImage),
    season_ids: selectedSeasonIdsForStockImage(stockImage),
  }
}

function createFilePreviewUrl(file) {
  const urlApi = globalThis.URL

  if (
    typeof File === 'undefined' ||
    !(file instanceof File) ||
    typeof urlApi?.createObjectURL !== 'function'
  ) {
    return null
  }

  return urlApi.createObjectURL(file)
}

function revokeFilePreviewUrl(previewUrl) {
  if (!previewUrl || typeof globalThis.URL?.revokeObjectURL !== 'function') {
    return
  }

  globalThis.URL.revokeObjectURL(previewUrl)
}

function StockImagePreview({ url, label, hasSelectedFile, canEdit, onEdit }) {
  const [status, setStatus] = useState('loading')

  if (!url || status === 'error') {
    return (
      <MediaPlaceholder
        icon={ImageIcon}
        label={
          hasSelectedFile
            ? 'Save to preview and edit this image.'
            : url
              ? 'Image preview unavailable'
              : 'No image selected'
        }
        className="aspect-square w-full"
        iconClassName="size-10"
      />
    )
  }

  return (
    <div className="group relative">
      <ImageTileControls
        canEdit={status === 'loaded' && canEdit}
        editLabel="Edit stock image"
        onEdit={onEdit}
      />
      <img
        src={url}
        alt={label}
        className="w-full rounded-lg border bg-muted object-contain"
        decoding="async"
        onLoad={() => setStatus('loaded')}
        onError={() => setStatus('error')}
      />
    </div>
  )
}

export function StockImageForm({ mode, stockImage, merchandisingTagGroups = [] }) {
  const isCreate = mode === 'create'
  const createMutation = useCreateStockImageMutation()
  const updateMutation = useUpdateStockImageMutation()
  const { data: tagsResponse } = useStockImageTags()
  const bannerTagId = Number(
    (tagsResponse?.data ?? []).find((tag) => tag.key === bannerTagKey)?.id ?? 0
  )
  const mutation = isCreate ? createMutation : updateMutation
  const { isReadOnly } = useIsReadOnly()
  const navigate = useNavigate()
  const path = usePathHelper()
  const [isEditorOpen, setIsEditorOpen] = useState(false)
  const [previewUrl, setPreviewUrl] = useState(null)
  const previewUrlRef = useRef(null)
  const form = useAppForm({
    defaultValues: formValuesForStockImage(stockImage),
    validators: {
      onSubmit: isCreate ? stockImageCreateSchema : stockImageSchema,
      onSubmitAsync: async ({ value }) => {
        if (isReadOnly) {
          return
        }

        try {
          if (isCreate) {
            const response = await createMutation.mutateAsync(value)
            const stockImageId = response?.data?.id

            toast.success('Stock image created')

            if (stockImageId) {
              await navigate({
                ...path.stockImages.show(stockImageId).link,
                ignoreBlocker: true,
              })
            } else {
              await navigate({ ...path.stockImages.index().link, ignoreBlocker: true })
            }

            return
          }

          const response = await updateMutation.mutateAsync({
            stockImageId: stockImage.id,
            values: {
              title: value.title,
              description: value.description,
              is_published: value.is_published,
              tag_ids: value.tag_ids,
              occasion_ids: value.occasion_ids,
              season_ids: value.season_ids,
              file: value.file,
            },
          })
          form.reset(
            response?.data ? formValuesForStockImage(response.data) : { ...value, file: null }
          )
          revokeFilePreviewUrl(previewUrlRef.current)
          previewUrlRef.current = null
          setPreviewUrl(null)
          toast.success('Stock image saved')
        } catch (error) {
          toast.error(isCreate ? 'Failed to create stock image' : 'Failed to save stock image', {
            description: getErrorMessage(error),
          })

          if (error?.response?.data?.errors) {
            return { fields: error.response.data.errors }
          }
        }
      },
    },
  })
  const selectedFile = useStore(form.store, (state) => state.values.file)
  const selectedTagIds = useStore(form.store, (state) => state.values.tag_ids)
  const selectedOccasionIds = useStore(form.store, (state) => state.values.occasion_ids)
  const selectedSeasonIds = useStore(form.store, (state) => state.values.season_ids)
  const merchandisingTagOptions = flattenMerchandisingTagGroups(merchandisingTagGroups)
  const selectedMerchandisingTagIds = [...selectedOccasionIds, ...selectedSeasonIds]
  const isBanner = bannerTagId > 0 && selectedTagIds.some((tagId) => Number(tagId) === bannerTagId)
  const uploadProfile = getMediaUploadProfile(
    isBanner ? MediaUploadUsage.StorefrontHero.Value : MediaUploadUsage.ImageStandard.Value
  )
  const imageAccept = uploadProfile.restrictions.allowedFileTypes.join(',')
  const media = getAssetMedia(stockImage)
  const displayedImageUrl = previewUrl || media.url
  const editorMedia = displayedImageUrl
    ? {
        ...media,
        id: media.id ?? stockImage?.id ?? 'stock-image-draft',
        name: selectedFile?.name || media.name || stockImage?.title || 'Stock image',
        mime_type: selectedFile?.type || media.mime_type,
        url: displayedImageUrl,
      }
    : null
  const canEditDisplayedImage = !isReadOnly && canEditMedia(editorMedia)

  const handleCancel = () => {
    void navigate({ ...path.stockImages.index().link, ignoreBlocker: true })
  }

  const setSelectedFile = (file) => {
    revokeFilePreviewUrl(previewUrlRef.current)
    const nextPreviewUrl = createFilePreviewUrl(file)

    previewUrlRef.current = nextPreviewUrl
    setPreviewUrl(nextPreviewUrl)
    form.setFieldValue('file', file)
  }

  const handleSaveEditedFile = async ({ file }) => {
    setSelectedFile(file)
  }

  const updateMerchandisingTags = (nextTagIds) => {
    const { occasionIds, seasonIds } = splitMerchandisingTagIds(
      nextTagIds,
      merchandisingTagOptions,
      {
        occasionIds: selectedOccasionIds,
        seasonIds: selectedSeasonIds,
      }
    )

    form.setFieldValue('occasion_ids', occasionIds)
    form.setFieldValue('season_ids', seasonIds)
  }

  useEffect(
    () => () => {
      revokeFilePreviewUrl(previewUrlRef.current)
    },
    []
  )

  return (
    <form.AppForm>
      <form.Form className="grid gap-5 xl:grid-cols-[minmax(0,1.25fr)_minmax(20rem,0.75fr)] xl:items-start">
        <div className="space-y-5">
          <StockImageLicensingNotice />

          <Card>
            <CardHeader>
              <CardTitle>Stock Image Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              {isCreate ? (
                <form.AppField name="file">
                  {() => (
                    <FileDropzone
                      accept={imageAccept}
                      label="Image"
                      description={uploadProfile.note}
                      file={selectedFile}
                      disabled={isReadOnly}
                      onFileSelect={setSelectedFile}
                      onClear={() => setSelectedFile(null)}
                    />
                  )}
                </form.AppField>
              ) : null}

              <form.AppField name="title">
                {(field) => (
                  <field.TextInput label="Title" placeholder="Baby's Breath" maxLength={120} />
                )}
              </form.AppField>

              <form.AppField name="description">
                {(field) => (
                  <field.TextareaInput
                    label="Description"
                    placeholder="A soft white floral detail that works well for weddings, sympathy, and airy arrangements."
                    rows={4}
                    maxLength={1000}
                  />
                )}
              </form.AppField>

              <form.AppField name="is_published">
                {(field) => (
                  <field.SwitchInput
                    label="Published"
                    description="Published stock images appear in the Photo Manager for florists."
                  />
                )}
              </form.AppField>
            </CardContent>
          </Card>

          <StockTagManager
            disabled={isReadOnly}
            selectedTagIds={selectedTagIds}
            onSelectedTagIdsChange={(tagIds) => form.setFieldValue('tag_ids', tagIds)}
          />

          <Card>
            <CardHeader>
              <CardTitle>Occasions</CardTitle>
              <CardDescription>
                Choose the holidays, seasons, or everyday occasions where this image can be used.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form.AppField name="occasion_ids">
                {(field) => (
                  <field.OccasionPicker
                    options={merchandisingTagOptions}
                    value={selectedMerchandisingTagIds}
                    writeToField={false}
                    label={null}
                    dialogTitle="Choose Occasions"
                    onValueChange={updateMerchandisingTags}
                  />
                )}
              </form.AppField>
            </CardContent>
          </Card>

          {isReadOnly ? (
            <div className="flex justify-end">
              <Button type="button" variant="outline" onClick={handleCancel}>
                Back
              </Button>
            </div>
          ) : null}

          {!isReadOnly ? <SaveBar mutation={mutation} /> : null}
        </div>

        <aside className="space-y-4 xl:sticky xl:top-4">
          <Card>
            <CardHeader>
              <CardTitle>Preview</CardTitle>
            </CardHeader>
            <CardContent>
              <StockImagePreview
                key={displayedImageUrl || 'empty'}
                url={displayedImageUrl}
                label={stockImage?.title || selectedFile?.name || 'Stock image preview'}
                hasSelectedFile={Boolean(selectedFile)}
                canEdit={canEditDisplayedImage}
                onEdit={() => setIsEditorOpen(true)}
              />

              {selectedFile ? (
                <Typography variant="supporting" className="mt-3 block">
                  {isCreate
                    ? `${selectedFile.name} is selected. Save to create this stock image.`
                    : `${selectedFile.name} is ready. Save to update this stock image.`}
                </Typography>
              ) : null}

              {media.width && media.height ? (
                <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <dt className="text-muted-foreground">Dimensions</dt>
                    <dd className="font-medium">
                      {media.width} x {media.height}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Type</dt>
                    <dd className="font-medium">{media.mime_type || 'Image'}</dd>
                  </div>
                </dl>
              ) : null}
            </CardContent>
          </Card>
        </aside>

        <LazyImageEditorDialog
          open={isEditorOpen}
          onOpenChange={setIsEditorOpen}
          media={editorMedia}
          description="Save in the editor prepares an edited stock image draft. Use the page Save button to publish the change."
          saveEditedFile={handleSaveEditedFile}
          savingMessage="Preparing edited stock image..."
          successMessage="Edited stock image ready to save"
        />
      </form.Form>
    </form.AppForm>
  )
}
